/**
 * Update Kiosk Settings Use Case
 * Business logic for updating kiosk settings with delta calculation
 */

import { KioskSettingsRepository } from '../data/kiosk-settings-repository';
import { KioskSettingsDto, UpdateKioskSettingsRequest } from '../types/kiosk-settings-types';
import { calculateDelta } from '../utils/delta-calculation';

/**
 * Use case for updating kiosk settings
 * Calculates delta (only changed fields) and sends to API
 */
export class UpdateKioskSettingsUseCase {
  constructor(private repository: KioskSettingsRepository) {}

  /**
   * Execute: Update kiosk settings
   * Calculates delta between original and current, sends only changes to API
   * @param operatorId - The operator ID
   * @param current - Current settings with potential changes
   * @param original - Original settings to compare against
   * @returns Promise<void>
   * @throws Error if validation fails or API call fails
   */
  async execute(
    operatorId: number,
    current: KioskSettingsDto,
    original: KioskSettingsDto
  ): Promise<void> {
    // Validate inputs
    if (!operatorId || operatorId <= 0) {
      throw new Error('Invalid operator ID: must be a positive number');
    }

    if (!current) {
      throw new Error('Current settings required');
    }

    if (!original) {
      throw new Error('Original settings required for delta calculation');
    }

    // Calculate delta to check if changes exist
    const delta = calculateDelta(original, current);

    // Validate that changes exist
    if (Object.keys(delta).length === 0) {
      throw new Error('No changes to save: current settings match original settings');
    }

    try {
      // Send full settings object to API (Flutter API expects full DbActor)
      await this.repository.updateSettings(operatorId, current);
    } catch (error) {
      throw error;
    }
  }
}
